// @refresh reload

import * as Sentry from "@sentry/solid"
import { render } from "solid-js/web"
import { AppBaseProviders, AppInterface } from "@/app"
import { type Platform, PlatformProvider } from "@/context/platform"
import { dict as en } from "@/i18n/en"
import { dict as zh } from "@/i18n/zh"
import { handleNotificationClick } from "@/utils/notification-click"
import { authFromToken } from "@/utils/server"
import { bootstrapCentralState, centralRuntime } from "@/utils/central-state"
import { prepareCentralTheme } from "@/utils/central-theme"
import { matchingBrowserState } from "@/utils/central-import"
import { captureSignupInvitation } from "@/utils/signup-invitation"
import { ErrorPage } from "@/pages/error"
import pkg from "../package.json"
import { ServerConnection } from "./context/server"
import { getPinchZoomEnabled, setPinchZoomEnabled, webviewZoom } from "./utils/browser-zoom"

const DEFAULT_SERVER_URL_KEY = "opencode.settings.dat:defaultServerUrl"
captureSignupInvitation({ url: location.href, replace: (url) => history.replaceState(history.state, "", url) })

const getLocale = () => {
  if (typeof navigator !== "object") return "en" as const
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    if (!language) continue
    if (language.toLowerCase().startsWith("zh")) return "zh" as const
  }
  return "en" as const
}

const getRootNotFoundError = () => {
  const key = "error.dev.rootNotFound" as const
  const locale = getLocale()
  return locale === "zh" ? (zh[key] ?? en[key]) : en[key]
}

const getStorage = (key: string) => {
  if (centralRuntime.get().mode === "central" || typeof localStorage === "undefined") return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setStorage = (key: string, value: string | null) => {
  if (centralRuntime.get().mode === "central" || typeof localStorage === "undefined") return
  try {
    if (value !== null) {
      localStorage.setItem(key, value)
      return
    }
    localStorage.removeItem(key)
  } catch {
    return
  }
}

const readDefaultServerUrl = () => getStorage(DEFAULT_SERVER_URL_KEY)
const writeDefaultServerUrl = (url: string | null) => setStorage(DEFAULT_SERVER_URL_KEY, url)

const notify: Platform["notify"] = async (title, description, href) => {
  if (!("Notification" in window)) return

  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission().catch(() => "denied")
      : Notification.permission

  if (permission !== "granted") return

  const inView = document.visibilityState === "visible" && document.hasFocus()
  if (inView) return

  const notification = new Notification(title, {
    body: description ?? "",
    icon: "/icon.jpeg",
  })

  notification.onclick = () => {
    handleNotificationClick(href)
    notification.close()
  }
}

const openLink: Platform["openLink"] = (url) => {
  window.open(url, "_blank")
}

const back: Platform["back"] = () => {
  window.history.back()
}

const forward: Platform["forward"] = () => {
  window.history.forward()
}

const restart: Platform["restart"] = async () => {
  window.location.reload()
}

const root = document.getElementById("root")
if (!(root instanceof HTMLElement) && import.meta.env.DEV) {
  throw new Error(getRootNotFoundError())
}

const getCurrentUrl = () => {
  if (location.hostname.includes("opencode.ai")) return "http://localhost:4096"
  if (import.meta.env.DEV)
    return `http://${import.meta.env.VITE_OPENCODE_SERVER_HOST ?? "localhost"}:${import.meta.env.VITE_OPENCODE_SERVER_PORT ?? "4096"}`
  return window.location.origin
}

const getDefaultUrl = () => {
  const lsDefault = readDefaultServerUrl()
  if (lsDefault) return lsDefault
  return getCurrentUrl()
}

const clearAuthToken = () => {
  const params = new URLSearchParams(location.search)
  if (!params.has("auth_token")) return
  params.delete("auth_token")
  history.replaceState(null, "", location.pathname + (params.size ? `?${params}` : "") + location.hash)
}

const platform: Platform = {
  platform: "web",
  version: pkg.version,
  openLink,
  back,
  forward,
  restart,
  notify,
  getDefaultServer: async () => {
    const stored = readDefaultServerUrl()
    return stored ? ServerConnection.Key.make(stored) : null
  },
  setDefaultServer: writeDefaultServerUrl,
  webviewZoom,
  getPinchZoomEnabled,
  setPinchZoomEnabled,
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? `web@${pkg.version}`,
    initialScope: {
      tags: {
        platform: "web",
      },
    },
    integrations: (integrations) => {
      return integrations.filter(
        (i) =>
          i.name !== "Breadcrumbs" && !(import.meta.env.OPENCODE_CHANNEL === "prod" && i.name === "GlobalHandlers"),
      )
    },
  })
}

async function start(root: HTMLElement) {
  await bootstrapCentralState({
    url: getCurrentUrl(),
    fetch,
    requireCentral: import.meta.env.VITE_OPENCODE_CENTRAL === "true",
  })
  const user = centralRuntime.get().user
  const browserImport = user ? matchingBrowserState(user, () => localStorage) : undefined
  if (centralRuntime.get().mode === "central") {
    try {
      localStorage.removeItem("omai_auth_token")
      // Keep the legacy owner hint until its browser data is migrated; it is never session authority.
    } catch {
      /* Storage may be disabled; authentication never depends on these legacy keys. */
    }
  }
  const themeStorage = await prepareCentralTheme()
  const auth =
    centralRuntime.get().mode === "central"
      ? undefined
      : authFromToken(new URLSearchParams(location.search).get("auth_token"))
  clearAuthToken()
  const server: ServerConnection.Http = {
    type: "http",
    authToken: !!auth,
    http: {
      url: getCurrentUrl(),
      ...auth,
    },
  }
  root.textContent = ""
  render(
    () => (
      <PlatformProvider value={platform}>
        <AppBaseProviders themeStorage={themeStorage} browserImport={browserImport}>
          <AppInterface
            defaultServer={ServerConnection.Key.make(getDefaultUrl())}
            canonicalLocalServer={ServerConnection.key(server)}
            servers={[server]}
            disableHealthCheck
          />
        </AppBaseProviders>
      </PlatformProvider>
    ),
    root,
  )
}

if (root instanceof HTMLElement) {
  void start(root).catch(async (error: unknown) => {
    // Failed bootstrap cannot make local storage authoritative. Reuse the original error/restart screen.
    centralRuntime.configure({ mode: "central", url: getCurrentUrl() })
    const themeStorage = await prepareCentralTheme()
    root.textContent = ""
    render(
      () => (
        <PlatformProvider value={platform}>
          <AppBaseProviders themeStorage={themeStorage}>
            <ErrorPage error={error} />
          </AppBaseProviders>
        </PlatformProvider>
      ),
      root,
    )
  })
}
